import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Grid,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { saveBusinessDefinition, updateBusinessDefinitionNameById } from 'wisestep-api';
import {
  WsButton,
  WsDialog,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsSelect,
  WsSelectObj,
  WsColors,
  Item,
  ControlledTextInput,
} from 'wisestep-base';
import { object, string } from 'yup';

import { useStageStatusContext } from '../../../features/submissions/context/StageStatusContext';
import useCandidateStatusesByAllStage from '../../../hooks/useCandidateStatuesByAllStages';
import { getAllCandidateStagesInWsSelectObj } from '../../../utils/CandidateStageStatusHelper';
import { IBusinessData, IStageData } from '../customizeStageStatus/helper';

interface IBusinessStageStatusProps {
  setOpenStatusModal: (action: boolean) => void;
  isOpenModal: boolean;
  setOpenSubStatus: (action: boolean) => void;
  isSubStatusOpenModal: boolean;
  setOpenBusiness: (action: boolean) => void;
  isBusinessOpenModal: boolean;
  businessData: (data: IBusinessData) => void;
  setName: (name: string) => void;
  businessName: string;
  setJobOptions: (jobOption: string) => void;
  jobOption: string;
  attachBusiness: IBusinessData | undefined;
  stageValues: IStageData | undefined;
  setStageData: (data: IStageData) => void;
  isRename: boolean;
  fetchAll: () => void;
  setIsRename: (isRename: boolean) => void;
}

export const jobOptions: WsSelectObj[] = [
  { label: 'All Jobs', value: 'all_jobs' },
  { label: 'Open Jobs', value: 'open_jobs' },
  { label: 'Closed Jobs', value: 'closed_jobs' },
];

const BusinessStageStatusPopUp = ({
  setOpenStatusModal,
  isOpenModal,
  setOpenSubStatus,
  isSubStatusOpenModal,
  setOpenBusiness,
  isBusinessOpenModal,
  businessData,
  setName,
  businessName,
  setJobOptions,
  jobOption,
  attachBusiness,
  stageValues,
  isRename,
  fetchAll,
  setIsRename,
  setStageData,
}: IBusinessStageStatusProps) => {
  const [stages, setStages] = useState<WsSelectObj[] | undefined>([]);
  const [checkedStatuses, setCheckedStatuses] = useState<string[]>(
    stageValues?.selectedStatuses ?? []
  );

  const onClose = () => {
    setOpenStatusModal(false);
    setOpenBusiness(true);
  };
  const onCloseStatus = () => {
    setCheckedStatuses([]);
    setStages([]);
    setOpenBusiness(false);
    setOpenSubStatus(false);
    setOpenStatusModal(true);
  };
  const closeAll = () => {
    setCheckedStatuses([]);
    setOpenBusiness(false);
    setOpenSubStatus(false);
    setOpenStatusModal(false);
  };
  const onCloseBusinessModal = () => setOpenBusiness(false);
  const stageStatus = useStageStatusContext();

  useEffect(() => {
    const stageObj = getAllCandidateStagesInWsSelectObj(stageStatus);
    const updatedStages = attachBusiness?.stageStatusData
      ? stageObj.filter((stage) => {
          return !attachBusiness?.stageStatusData?.some(
            (attachStageItem) => attachStageItem.stage === stage.label
          );
        })
      : stageObj;

    setStages(updatedStages);
  }, [attachBusiness?.stageStatusData, stageStatus]);

  const schema = object().shape({
    businessName: string().required('Business Definition name is required'),
    jobOptions: string().required('Job option is required'),
  });

  const defaultValue = {
    businessName: isRename ? businessName : '',
    stage: stageValues?.stage,
    jobOptions: isRename ? jobOption : 'all_jobs',
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValue,
  });

  const { watch } = methods;
  const selectedStage = watch('stage') ?? '';
  const statuses = useCandidateStatusesByAllStage(
    stageStatus?.stageStatusPipeline ?? '',
    selectedStage ?? stageValues?.stage
  );

  const changeToStatues = () => {
    setStages([]);
    setName(businessName);
    setJobOptions(jobOption);
    onClose();
    setOpenSubStatus(true);
  };

  const handleCheckboxChange = (option: string | number) => {
    const optionStr = String(option);
    setCheckedStatuses((prevSelected) => {
      if (prevSelected.includes(optionStr)) {
        return prevSelected.filter((selected) => selected !== optionStr);
      } else {
        return [...prevSelected, optionStr];
      }
    });
  };

  const handleSubmit = () => {
    setOpenSubStatus(false);
    setOpenStatusModal(false);
    setOpenBusiness(false);
    const stageData: IStageData[] = [];

    const existingIndex = attachBusiness?.stageStatusData?.findIndex(
      (item) => item.stage === stageValues?.stage
    );

    const tempStage = attachBusiness?.stageStatusData;
    if (tempStage && existingIndex !== undefined && existingIndex !== -1) {
      tempStage[existingIndex] = {
        stage: stageValues?.stage ?? '',
        selectedStatuses: checkedStatuses,
      };
    } else {
      stageData?.push({
        stage: selectedStage,
        selectedStatuses: checkedStatuses,
      });
    }
    const mergedArray: IStageData[] = [...stageData, ...(attachBusiness?.stageStatusData ?? [])];
    const businessDefinition: IBusinessData = {
      businessDefinitionName: attachBusiness?.businessDefinitionName ?? businessName,
      stageStatusData: mergedArray,
      id: attachBusiness?.id,
      createdBy: attachBusiness?.createdBy,
      createdOn: attachBusiness?.createdOn,
      isArchive: attachBusiness?.isArchive,
      jobOption: jobOption ?? attachBusiness?.jobOption,
    };
    businessData(businessDefinition);
    setStageData({ selectedStatuses: [], stage: '' });
  };

  const saveName = async () => {
    onCloseBusinessModal();
    const businessDefinition: IBusinessData = {
      businessDefinitionName: watch('businessName'),
      jobOption: watch('jobOptions') ?? 'all_jobs',
      isArchive: false,
      stageStatusData: [],
    };
    await saveBusinessDefinition(businessDefinition);
    fetchAll();
  };

  const onRename = async () => {
    const name = watch('businessName');
    const option = watch('jobOptions');
    await updateBusinessDefinitionNameById(attachBusiness?.id ?? '', name, option);
    onCloseBusinessModal();
    businessData({ stageStatusData: [], businessDefinitionName: '' });
    setIsRename(false);
    fetchAll();
  };
  return (
    <>
      <FormProvider {...methods}>
        <WsDialog openDialog={isBusinessOpenModal} size="small">
          <Grid container flexDirection="column">
            <DialogContent>
              <Item sx={{ pl: '45%', pr: '45%' }}>
                <SaveOutlinedIcon sx={{ color: WsColors.TheBlues400, fontSize: '60px' }} />
              </Item>
              <Item sx={{ margin: 'auto', py: 0, my: 0 }}>
                <WsTypography bold variant={WsTypoVariants.TITLE}>
                  Enter a name for your Business Definition
                </WsTypography>
              </Item>
              <Item sx={{ margin: 'auto' }}>
                <ControlledTextInput
                  name="businessName"
                  placeholder="Enter Business Definition Name"
                  required
                  fullWidth
                />
              </Item>
              <Item sx={{ margin: 'auto', py: 0, my: 0 }}>
                <WsTypography bold variant={WsTypoVariants.TITLE}>
                  Select a scope for which it should be applicable
                </WsTypography>
              </Item>
              <Item sx={{ pr: '65%' }}>
                <WsSelect
                  name="jobOptions"
                  defaultValue="closed_jobs"
                  selectArray={jobOptions ?? []}
                />
              </Item>
            </DialogContent>
            <DialogActions>
              <WsButton size="large" variant="outlined" onClick={() => setOpenBusiness(false)}>
                Cancel
              </WsButton>
              {isRename ? (
                <WsButton size="large" variant="contained" onClick={methods.handleSubmit(onRename)}>
                  Save
                </WsButton>
              ) : (
                <WsButton size="large" variant="contained" onClick={methods.handleSubmit(saveName)}>
                  Save
                </WsButton>
              )}
            </DialogActions>
          </Grid>
        </WsDialog>

        <WsDialog size="small" openDialog={isOpenModal}>
          <DialogContent>
            <Grid container justifyContent="flex-end">
              <CloseIcon sx={{ cursor: 'pointer' }} fontSize="medium" onClick={closeAll} />
            </Grid>
            <Grid container justifyContent="center" direction="column" alignItems="center">
              <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK} bold>
                Select a Stage
              </WsTypography>
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                Select a Stage which you want to add to the Business Definition
              </WsTypography>
            </Grid>
            <Grid container justifyContent="center" pt={4} pb={2}>
              <Grid spacing={2} p={2} xs={5}>
                <WsSelect name="stage" selectArray={stages ?? []} />
              </Grid>
            </Grid>
            <Grid container justifyContent="space-between">
              <Grid item>
                <WsButton onClick={onClose} type="button" size="medium" variant="outlined">
                  Go Back
                </WsButton>
              </Grid>
              <Grid item>
                <WsButton onClick={changeToStatues} type="button" size="medium">
                  Next
                </WsButton>
              </Grid>
            </Grid>
          </DialogContent>
        </WsDialog>

        <WsDialog size="small" openDialog={isSubStatusOpenModal}>
          <DialogContent style={{ overflowX: 'hidden' }}>
            <Grid container justifyContent="flex-end">
              <CloseIcon sx={{ cursor: 'pointer' }} fontSize="medium" onClick={closeAll} />
            </Grid>
            <Grid container justifyContent="center" direction="column" alignItems="center">
              <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK} bold>
                Select a Statues
              </WsTypography>
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                Select a Statues which you want to add to the Business Definition
              </WsTypography>
            </Grid>
            <Grid container justifyContent="flex-start" ml={4} pt={1} pb={2}>
              <FormGroup>
                {statuses.map((data) => (
                  <FormControlLabel
                    key={data.value}
                    control={
                      <Checkbox
                        defaultChecked={stageValues?.selectedStatuses.includes(
                          data.value.toString()
                        )}
                        onChange={() => handleCheckboxChange(data.value)}
                      />
                    }
                    label={
                      <WsTypography
                        variant={WsTypoVariants.SUB_TITLE}
                        color={WsTypoColors.PRIMARY}
                        cursor="pointer"
                      >
                        {data.value}
                      </WsTypography>
                    }
                  />
                ))}
              </FormGroup>
            </Grid>
            <Grid container justifyContent="space-between">
              <Grid item>
                <WsButton onClick={onCloseStatus} type="button" size="medium" variant="outlined">
                  Go Back
                </WsButton>
              </Grid>
              <Grid item>
                <WsButton onClick={handleSubmit} type="button" size="medium">
                  Next
                </WsButton>
              </Grid>
            </Grid>
          </DialogContent>
        </WsDialog>
      </FormProvider>
    </>
  );
};
export default BusinessStageStatusPopUp;
